/**
 * assets/notify-me-klaviyo.js
 * ---------------------------------------------------------------------------
 * Powers the custom "Notify Me When Available" popover
 * (snippets/notify-me-klaviyo.liquid) and submits the shopper's email to
 * Klaviyo as a back-in-stock subscription for the SIZE THEY CHOSE.
 *
 * Opening and closing the popover is handled natively by the theme's
 * <x-drawer-popover> component, so this file handles three things: keeping the
 * size dropdown in step with the product page, submitting the subscription, and
 * swapping in the confirmation message.
 *
 * TROUBLESHOOTING: add ?notify_debug=1 to the product page URL and submit the
 * form again. The popup will then show the raw error instead of the friendly
 * message. Shoppers never see it. To confirm signups are really landing, check
 * the "Subscribed to Back in Stock" metric in Klaviyo — and check the VariantName
 * on the event, not just that an event exists.
 *
 * WHY THIS USES THE ONSITE ENDPOINT (important context, do not "modernise" this
 * without testing): Klaviyo's newer Back In Stock Subscriptions API
 * (/client/back-in-stock-subscriptions/) references products by CATALOG VARIANT
 * id, e.g. "$shopify:::$default:::<variant id>". This account has no
 * API-addressable catalog — the Catalog API returns zero items and rejects the
 * "$shopify" integration entirely — because product data arrives through the
 * native Shopify integration instead. Calls to that API are accepted with a 202
 * and then silently discarded: no subscription, no event, no flow.
 *
 * The endpoint below is the one Klaviyo's own Shopify back-in-stock button posts
 * to, and it is verified working against this account. The subscriptions it
 * creates are indistinguishable from organic ones: Klaviyo resolves the plain
 * Shopify variant id and enriches the event with ProductName, VariantName, SKU,
 * Price, Categories and Tags, exactly as it does for signups made through its
 * own form.
 *
 * WHICH FLOW THESE SIGNUPS FEED: "FW26 Back In Stock Flow" (WVvW2m).
 *
 * That is documentation, not configuration. A subscription cannot be addressed
 * to a particular flow, and nothing in this file names one. Klaviyo routes purely
 * by trigger: every LIVE flow triggered on the "Subscribed to Back in Stock"
 * metric receives these signups. As of 22 September 2026 exactly one such flow is
 * live — FW26 — and the other three on that metric are draft, so routing is
 * unambiguous. To change which flow handles back-in-stock, change flow statuses in
 * Klaviyo, never this file. If a second flow on that metric is ever set live,
 * subscribers will get one email from each.
 *
 * THE SIZE IS WHATEVER THE SHOPPER PICKS IN THE DROPDOWN — NOTHING PRE-SELECTS IT.
 *
 * A previous version tried to be helpful by pre-selecting the size showing on the
 * product page, and re-syncing it on every change event. That silently overwrote
 * the shopper's own choice: both listeners were on document with capture, the sync
 * one was registered first, so on the very change event where the shopper picked
 * "L" the sync ran BEFORE the "don't touch it again" flag was set and reset the
 * dropdown to the page's variant. On a fully sold-out product that variant is
 * Liquid's fallback — the first one, XS. Verified live: a signup intended for L
 * recorded XS. The pre-select is gone. Do not reintroduce it without a test that
 * picks a size other than the first one and checks VariantName on the event.
 *
 * THE MARKETING OPT-IN IS A SEPARATE, REAL SUBSCRIPTION. When the shopper ticks
 * the newsletter box, this file additionally calls Klaviyo's client subscribe
 * endpoint to record an explicit SUBSCRIBED consent and add them to the
 * Newsletter list. That is a different endpoint and a different kind of
 * permission from the back-in-stock alert, and it runs only when the box is
 * ticked. An earlier version called klaviyo.identify() for this, which grants no
 * consent and joins no list — the box collected nothing.
 *
 * What this file does NOT do: send the email. It only records the back-in-stock
 * subscription. The "it's back!" email is sent by the Klaviyo flow when that
 * variant's inventory returns.
 */

(function () {
  /* =========================================================================
   * KLAVIYO SETTINGS
   * =======================================================================*/

  // 1. PUBLIC API KEY (a.k.a. company ID / site ID). Leave as null to read it
  //    automatically from the Klaviyo onsite script already installed on the
  //    store. Only set a value here if that auto-detection ever fails:
  //    const KLAVIYO_PUBLIC_API_KEY = 'ABC123';
  const KLAVIYO_PUBLIC_API_KEY = null;

  // 2. ENDPOINT. Klaviyo's onsite back-in-stock subscribe endpoint. Takes
  //    form-encoded fields and the plain Shopify variant ID (no catalog prefix).
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/onsite/components/back-in-stock/subscribe';

  // 3. PLATFORM. Tells Klaviyo to resolve the variant ID against the Shopify
  //    integration.
  const KLAVIYO_PLATFORM = 'shopify';

  // 4. EMAIL MARKETING OPT-IN. Where the "Also send me Spyder news" checkbox
  //    subscribes people. Pzwe4X is the Newsletter list — the same list Klaviyo's
  //    own back-in-stock form used to add people to. To stop the checkbox
  //    subscribing anyone, set KLAVIYO_LIST_ID to null; the box will then do
  //    nothing and should be removed from the popover too.
  const KLAVIYO_LIST_ID = 'Pzwe4X';
  const KLAVIYO_SUBSCRIBE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  const KLAVIYO_API_REVISION = '2026-07-15';

  //    Stamped onto the consent record so these signups are attributable in
  //    Klaviyo, and so this source can be excluded from campaigns if ever needed.
  const KLAVIYO_CONSENT_SOURCE = 'Back in Stock popup (FW26)';

  // 5. MESSAGES. All customer-facing copy lives in
  //    snippets/notify-me-klaviyo.liquid so it sits in one place. These are only
  //    fallbacks if the snippet doesn't supply them.
  const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Please try again in a moment.';
  const FALLBACK_NO_SIZE_MESSAGE = 'Please choose a size first.';

  /* =========================================================================
   * END OF SETTINGS
   * =======================================================================*/

  // A Klaviyo public key is a short alphanumeric string (e.g. "Q2rsat"). We validate
  // anything we find, because window.klaviyo is a stub object whose properties return
  // FUNCTIONS rather than values — reading window.klaviyo.account_id gives you a
  // function, which is truthy and would otherwise sail straight into the request.
  function isValidPublicKey(value) {
    return typeof value === 'string' && /^[A-Za-z0-9_-]{4,32}$/.test(value);
  }

  // The onsite script is served as either
  //   .../onsite/js/klaviyo.js?company_id=KEY      (older)
  //   .../onsite/js/KEY/klaviyo.js?company_id=KEY  (current)
  // so we check the query string first and then the path.
  function getKeyFromOnsiteScript() {
    const scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');

    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src || '';

      const fromQuery = src.match(/[?&]company_id=([A-Za-z0-9_-]+)/);
      if (fromQuery && isValidPublicKey(fromQuery[1])) return fromQuery[1];

      const fromPath = src.match(/\/onsite\/js\/([A-Za-z0-9_-]+)\/klaviyo\.js/);
      if (fromPath && isValidPublicKey(fromPath[1])) return fromPath[1];
    }

    return null;
  }

  // Klaviyo's legacy queue sometimes carries the account key: _learnq.push(['account', 'KEY'])
  function getKeyFromLearnq() {
    if (!Array.isArray(window._learnq)) return null;

    for (let i = 0; i < window._learnq.length; i++) {
      const entry = window._learnq[i];
      if (Array.isArray(entry) && entry[0] === 'account' && isValidPublicKey(entry[1])) {
        return entry[1];
      }
    }

    return null;
  }

  function getKlaviyoCompanyId() {
    if (isValidPublicKey(KLAVIYO_PUBLIC_API_KEY)) return KLAVIYO_PUBLIC_API_KEY;

    const fromScript = getKeyFromOnsiteScript();
    if (fromScript) return fromScript;

    if (window.klaviyo && isValidPublicKey(window.klaviyo.account_id)) {
      return window.klaviyo.account_id;
    }

    const fromLearnq = getKeyFromLearnq();
    if (fromLearnq) return fromLearnq;

    if (isValidPublicKey(window.KLAVIYO_COMPANY_ID)) return window.KLAVIYO_COMPANY_ID;

    return null;
  }

  async function subscribeBackInStock({ email, variantId }) {
    const companyId = getKlaviyoCompanyId();

    if (!companyId) {
      throw new Error(
        'Klaviyo public API key not found on the page. Check that the Klaviyo app embed is enabled for this theme, or set KLAVIYO_PUBLIC_API_KEY at the top of notify-me-klaviyo.js.'
      );
    }
    if (!email || !variantId) {
      throw new Error('Email and variant are both required.');
    }

    const params = new URLSearchParams();
    params.set('a', companyId);
    params.set('email', email);
    params.set('variant', variantId);
    params.set('platform', KLAVIYO_PLATFORM);

    // The onsite endpoint does not return CORS headers, so the browser will send
    // this request and let Klaviyo process it, but refuse to let us READ the
    // reply — a normal fetch rejects with "Failed to fetch" even though the
    // subscription was recorded successfully (verified against the account).
    // Posting in no-cors mode is therefore deliberate: it is a form-encoded
    // simple request, so it goes through untouched, and we accept an opaque
    // response instead of a readable one.
    //
    // The trade-off: we cannot inspect Klaviyo's reply, so a resolved request
    // means "accepted by the browser and sent", not "Klaviyo said yes". A genuine
    // network failure still rejects and is still reported. If the integration ever
    // needs re-verifying, check the "Subscribed to Back in Stock" metric in
    // Klaviyo rather than trusting this confirmation.
    if (navigator.onLine === false) {
      throw new Error('No network connection, so the subscription was not sent.');
    }

    try {
      await fetch(KLAVIYO_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });
    } catch (networkError) {
      throw new Error(
        `Could not reach Klaviyo (${networkError.message}). Sent: variant ${variantId}, key ${companyId}.`
      );
    }

    return true;
  }

  /**
   * Grants real email marketing consent and adds the profile to the Newsletter list.
   *
   * This is a DIFFERENT endpoint from the back-in-stock one above, and a different kind of
   * thing. The back-in-stock subscription is a one-off alert the shopper asked for; this is
   * ongoing marketing permission, so it needs an explicit consent record. Klaviyo's
   * identify() does NOT do this — it only associates a browsing session with a profile, which
   * is why the old version of this checkbox collected nothing.
   *
   * Unlike the onsite back-in-stock endpoint, /client/subscriptions is a documented
   * browser-facing API: it is designed to be called with the PUBLIC key from a storefront, and
   * it returns a readable response, so failures here are real failures and are reported.
   *
   * Only ever called when the shopper ticked the box.
   */
  async function subscribeToEmailMarketing({ email, companyId }) {
    if (!KLAVIYO_LIST_ID) return false;
    if (!email) throw new Error('Email is required to subscribe to marketing.');

    const url = `${KLAVIYO_SUBSCRIBE_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO_CONSENT_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: KLAVIYO_LIST_ID,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION,
      },
      body: JSON.stringify(payload),
    });

    // A successful subscribe returns 202 Accepted with an empty body.
    if (!response.ok) {
      let detail = '';

      try {
        detail = (await response.text()).slice(0, 500);
      } catch (readError) {
        detail = '(response body unreadable)';
      }

      throw new Error(`Klaviyo list subscribe failed (${response.status}). ${detail}`);
    }

    return true;
  }

  window.NotifyMeKlaviyo = {
    subscribe: subscribeBackInStock,
    subscribeToEmailMarketing: subscribeToEmailMarketing,
  };

  if (!customElements.get('notify-me-klaviyo')) {
    customElements.define(
      'notify-me-klaviyo',
      class NotifyMeKlaviyo extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('[data-notify-me-form]');
          this.message = this.querySelector('[data-notify-me-message]');
          this.intro = this.querySelector('[data-notify-me-intro]');
          this.success = this.querySelector('[data-notify-me-success]');
          this.variantSelect = this.querySelector('[name="variant_id"]');

          if (this.form && !this.form.dataset.notifyMeBound) {
            this.form.dataset.notifyMeBound = 'true';
            this.form.addEventListener('submit', (event) => this.onSubmit(event));
          }

        }

        // NOTE: there is deliberately NO code here that pre-selects a size from the
        // product page. An earlier version did, and it was actively harmful — see the
        // header comment. The dropdown always opens on the placeholder and the shopper
        // picks. Do not add a default back.

        async onSubmit(event) {
          event.preventDefault();
          if (!this.form) return;

          const submitBtn = this.form.querySelector('button[type="submit"]');
          const formData = new FormData(this.form);
          const email = formData.get('email');
          const variantId = formData.get('variant_id');
          const acceptsMarketing = formData.get('accepts_marketing') !== null;

          this.setMessage('', false);

          // Belt and braces: the select is `required`, but never let a submission
          // through without a size. Subscribing someone to the wrong variant means
          // they silently never get the email.
          if (!variantId) {
            this.setMessage(this.dataset.noSizeMessage || FALLBACK_NO_SIZE_MESSAGE, true);
            if (this.variantSelect && this.variantSelect.focus) this.variantSelect.focus();
            return;
          }

          if (submitBtn) submitBtn.disabled = true;

          try {
            // The back-in-stock alert comes first and is the thing the shopper actually asked
            // for. If it throws, we stop here and show an error — nothing else has run yet.
            await window.NotifyMeKlaviyo.subscribe({ email, variantId });

            // The newsletter opt-in is secondary and deliberately non-blocking. If it fails we
            // still confirm, because the alert they wanted IS registered; telling them
            // "something went wrong" would be a lie that also makes them submit again and
            // create a duplicate subscription. The failure is logged, and ?notify_debug=1
            // surfaces it.
            if (acceptsMarketing) {
              try {
                await window.NotifyMeKlaviyo.subscribeToEmailMarketing({
                  email,
                  companyId: getKlaviyoCompanyId(),
                });
              } catch (consentError) {
                console.error('[notify-me-klaviyo] marketing opt-in failed', consentError);

                if (/[?&]notify_debug=1/.test(window.location.search)) {
                  this.setMessage(String((consentError && consentError.message) || consentError), true);
                }
              }
            }

            this.showConfirmation();
          } catch (err) {
            console.error('[notify-me-klaviyo]', err);

            // Add ?notify_debug=1 to the page URL to show the raw error from Klaviyo
            // in the popup instead of the friendly message. Shoppers never see this;
            // it just saves opening DevTools.
            const showRawError = /[?&]notify_debug=1/.test(window.location.search);

            this.setMessage(
              showRawError
                ? String((err && err.message) || err)
                : this.dataset.errorMessage || FALLBACK_ERROR_MESSAGE,
              true
            );
          } finally {
            if (submitBtn) submitBtn.disabled = false;
          }
        }

        // Klaviyo accepted the subscription: swap the form out for the confirmation,
        // which carries its own close button (the theme's <dialog-close-button>).
        showConfirmation() {
          const chosen = this.selectedSizeLabel();

          this.form.reset();
          this.setMessage('', false);

          if (this.intro) this.intro.hidden = true;
          this.form.hidden = true;

          if (this.success) {
            // Name the size back to the shopper. This is the only confirmation they
            // get that the right variant was captured.
            const text = this.success.querySelector('[data-notify-me-success-text]');
            if (text && chosen && text.textContent.indexOf(chosen) === -1) {
              text.textContent = `${chosen}: ${text.textContent}`;
            }

            this.success.hidden = false;

            const closeButton = this.success.querySelector('button');
            if (closeButton) requestAnimationFrame(() => closeButton.focus());
          }
        }

        selectedSizeLabel() {
          if (!this.variantSelect || this.variantSelect.tagName !== 'SELECT') return '';

          const option = this.variantSelect.selectedOptions && this.variantSelect.selectedOptions[0];
          return option && option.value ? option.textContent.trim() : '';
        }

        setMessage(text, isError) {
          if (!this.message) return;
          this.message.textContent = text;
          this.message.hidden = !text;
          this.message.style.color = isError ? 'rgb(var(--error-text))' : '';
        }
      }
    );
  }
})();
